import React from 'react';
import { convertFieldsToGraphqlVariables } from '../../graphql-helpers';
import { get } from 'immutadot';
import { parse } from 'date-fns';
import * as XLSX from 'xlsx';
import { useCampaignsQueryQuery } from "../../generated/graphql";
import { ReportColumn } from "../report-column";
import { useToast } from "../../contexts/toast";
import { useLocation } from "react-router";
import QueryString from "query-string";


function dateToExcel(inDate) {
    const returnDateTime = 25569.0 + ((inDate.getTime() - (inDate.getTimezoneOffset() * 60 * 1000)) / (1000 * 60 * 60 * 24));
    return returnDateTime.toString().substr(0, 5);
}
type QSP = {
    name: string,
    type: string,
    value: any
};

export const DigitalBookingReport = ({ params, done }: { params?: any, done: () => void }) => {
    const location = useLocation();
    const rawParsedQueryString = QueryString.parse(location.search);
    const queryStringArray: QSP[] = [];
    Object.entries(rawParsedQueryString).forEach(function ([k, v]) {
        const name = k.split('-');
        queryStringArray.push({ name: name[0], type: name[1], value: v });
    });
    const gqlParams = convertFieldsToGraphqlVariables(queryStringArray);
    gqlParams.with_digital = true;
    //const toastId = 'cost-report';
    const { toast, dismiss } = useToast();
    toast('Generating report', { id: 'GEN_REPORT', timeout: 0 });
    const currency = { glyph: '£' };
    const query = useCampaignsQueryQuery({
        //useQuery(campaignsQuery,{
        variables: gqlParams,
        fetchPolicy: "network-only",
        onCompleted: (v) => {
            console.log('done', v);
            toast('Rendering report', { id: 'GEN_REPORT' });
            const pixPerLetter = 7;
            const rawValues = v.campaigns?.data || [];
            const tableValues = [];
            rawValues.forEach(row => {
                if (!row.digitalBookings?.length) return;
                row.digitalBookings.forEach((db) => {
                    const c: any = { ...db };
                    c.campaign = row;

                    const discountAmount = (c.campaign.discountableTotal) * (c.campaign.discount_percentage / 100);
                    const discountAmountGoodWill = c.campaign.discount_final;
                    let discountedTotalCampaign;

                    if (discountAmount !== null && discountAmountGoodWill !== null && discountAmount > 0 && discountAmountGoodWill > 0) {
                        discountedTotalCampaign = (c.campaign.discountableTotal) - (discountAmount + discountAmountGoodWill);
                    } else if (discountAmount !== null && discountAmount > 0) {
                        discountedTotalCampaign = (c.campaign.discountableTotal) - discountAmount;
                    } else if (discountAmountGoodWill !== null && discountAmountGoodWill > 0) {
                        discountedTotalCampaign = c.campaign.discountableTotal - discountAmountGoodWill;
                    } else {
                        discountedTotalCampaign = c.campaign.discountableTotal;
                    }
                    if (c.cost_type == 'direct') {
                        // Logic for c.revenue if Discounted Total exceeded from Discountable Total which gives us -ve number.
                        if (discountedTotalCampaign < 0) {
                            discountedTotalCampaign = Math.abs(discountedTotalCampaign);

                            const totalUnitValue = row.digitalBookings.reduce((sum, booking) => {
                                return sum + (booking.campaignComponent?.unit_value_local || 0);
                            }, 0);

                            c.revenue = c.campaignComponent?.unit_value_local - (((c.campaignComponent?.unit_value_local || 0) / totalUnitValue) * discountedTotalCampaign);
                        } else {
                            c.revenue = c.campaignComponent?.unit_value_local;
                        }
                    } else {
                        c.revenue = (discountedTotalCampaign / c.campaign.discountableTotal) * c.campaignComponent?.unit_value_local;
                    }

                    tableValues.push(c);
                });
            });
            console.log('values is', tableValues);
            const eData = [];
            const columns: ReportColumn[] = [
                {
                    accessor: "campaign.job_reference",
                    header: "Job Ref",
                },
                {
                    accessor: "campaign.status",
                    header: "Status",
                    width: "minmax(4em, max-content)",
                    dataType: "default",
                },
                {
                    accessor: "campaign.name",
                    header: "Job Name",
                    className: "column-width-wide-plus",
                    dataType: "default",
                },
                {
                    accessor: "campaign.salesTeam.name",
                    header: "Sales Team",
                    dataType: "default",
                },
                {
                    accessor: "campaign.ourContact.name",
                    header: "Account Manager",
                    dataType: "default",
                },
                {
                    accessor: "site.name",
                    header: "Site Name",
                    exportAs: "link",
                },
                {
                    accessor: "site.siteStructure.name",
                    header: "Site Structure",
                    dataType: "default",
                },
                {
                    accessor: "site.sitePricing.label",
                    header: "Ownership",
                    dataType: "default",
                },
                {
                    accessor: "start_date",
                    header: "Start Date",
                },
                {
                    accessor: "end_date",
                    header: "End Date",
                },
                {
                    accessor: "days",
                    header: "Total Days",
                },
                {
                    accessor: "cost_type",
                    header: "Cost Type",
                },
                {
                    accessor: "campaignComponent.region.name",
                    header: "Region Name",
                },
                {
                    accessor: "campaignComponent.value",
                    header: "Total Cost",
                    exportAs: "currency",
                },
                {
                    accessor: "revenue",
                    header: "Revenue",
                    exportAs: "currency",
                },
            ];
            const worksheet = { '!ref': 'A1:' };
            let col = 1;
            const colwidths = [];
            columns.forEach((column) => {
                const header = column.headerRaw ? column.headerRaw : column.header;
                worksheet[String.fromCharCode(64 + col) + 1] = { t: 's', v: header };
                colwidths.push({ wpx: header.toString().length });
                col++;
            });
            worksheet['!ref'] = 'A1:' + String.fromCharCode(64 + col) + '1';
            eData.push(columns.map((column) => column.header));
            let rowIdx = 2;
            let lastCell = '';
            tableValues.forEach((row) => {
                let col = 1;
                columns.forEach((column) => {
                    const cell: any = { t: 's', v: undefined, f: undefined, w: undefined, z: undefined };
                    let value: any = null;
                    value = get(row, column.sort ? column.sort : column.accessor);
                    const rawValue = value;
                    if (column.accessor == 'campaignComponent.value') {
                        const valueA = get(row, 'campaignComponent.unit_value_local');
                        const valueB = get(row, 'campaignComponent.qty');
                        value = (valueA ?? 0) * (valueB ?? 0);
                    }
                    switch (column.exportAs) {
                        case 'number':
                            cell.t = 'n';
                            cell.v = value;
                            if (isNaN(value) || value === null) {
                                cell.v = 0;
                            }
                            break;
                        case 'link':
                            if (row?.site?.externalLink) {
                                cell.t = 's';
                                cell.v = value;
                                cell.l = { Target: row.site.externalLink };
                            } else {
                                cell.v = value;
                            }
                            break;
                        case 'date-uk':
                            cell.t = 'n';
                            cell.z = 'dd/mm/yyyy';
                            var dateValue = parse(value, 'dd/MM/yyyy', new Date());
                            //console.log('parsing',value,parse(value, 'dd/MM/yyyy', new Date()),!!parse(value, 'dd/MM/yyyy', new Date()));
                            cell.v = isNaN(dateValue.getTime()) ? '' : dateToExcel(dateValue);
                            //value = parse(value, 'dd/MM/yyyy', new Date());
                            break;
                        case 'date-iso':
                            cell.t = 'n';
                            cell.z = 'dd/mm/yyyy';
                            //console.log('parsing',value,parse(value, 'yyyy-MM-dd', new Date()));
                            cell.v = dateToExcel(parse(value, 'yyyy-MM-dd', new Date()));
                            //value = parse(value, 'dd/MM/yyyy', new Date());
                            break;
                        case 'percentage':
                            cell.t = 'n';
                            //                        cell.v = parseFloat(value?value.replace('%', ''):'');
                            cell.z = '#.##"%"';
                            cell.v = value;
                            if (typeof value == 'number') {
                                cell.v = value / 100;
                            }
                            if (typeof value == 'string') {
                                cell.v = parseFloat(value ? value.replace('%', '') : '');
                            }
                            if (isNaN(cell.v) || cell.v == null) {
                                cell.v = 0;
                            }
                            //cell.w = value; 
                            //value = parseFloat(value.replace('%', ''));
                            break;
                        case 'currency':
                            cell.t = 'n';
                            cell.v = value;
                            if (typeof value == 'number') {
                                cell.v = value / 100;
                            }
                            if (typeof value == 'string') {
                                cell.v = parseFloat(value ? value.replace(/[^0-9\\.]/, '') : '');
                            }
                            if (isNaN(cell.v) || cell.v === null) {
                                cell.v = 0;
                            }
                            console.log('cell is', cell);
                            //                        if(!cell.v) { cell.v = value; }
                            cell.z = '"' + (currency?.glyph ? currency.glyph : '£') + '"' + '0.00';
                            //cell.w = value;
                            break;
                        case 'boolean':
                            if (typeof value == 'object') {
                                cell.v = rawValue ? 'Y' : 'N';
                            } else {
                                cell.v = value ? 'Y' : 'N';
                            }
                            break;
                        default:
                            if (typeof value == 'object') {
                                if (value) {
                                    value = value.toString();
                                    cell.v = value;
                                } else {
                                    cell.v = ''; value = '';
                                }
                            }
                            else {
                                cell.v = value;
                            }
                            break;
                    }
                    if (cell.v) {
                        colwidths[col - 1].wpx = Math.max(colwidths[col - 1].wpx, cell.v.toString().length);
                    }
                    lastCell = String.fromCharCode(64 + col++) + rowIdx;
                    worksheet[lastCell] = cell;
                    //newRow.push(value);
                });
                rowIdx++;
                //            return newRow;
            });
            worksheet['!ref'] = 'A1:' + lastCell;
            colwidths.forEach(v => { v.wpx = v.wpx * pixPerLetter; });
            console.log('widths', colwidths);
            worksheet['!cols'] = colwidths;
            console.log('worksheet is', worksheet);
            //if (eData.length > 1) {
            //const ws = XLSX.utils.aoa_to_sheet(eData);
            //var ws = XLSX.utils.aoa_to_sheet(eData);
            const wb = XLSX.utils.book_new();
            //var cell = ws['A2'];
            //console.log(worksheets);
            //worksheet['A2'] = {t:'s',v: 'TEST'};
            XLSX.utils.book_append_sheet(wb, worksheet, "SheetJS");
            XLSX.writeFile(wb, "sheetjs.xlsx");
            //}
            done();
        },
        onError: () => {
            toast('Error generating report', { id: 'GEN_REPORT', variant: 'error' });
            done();
        }
    });
    console.log(query);
    return null;
}